import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import {
  email,
  maxLength,
  minLength,
  ReferenceInput,
  regex,
  required,
  SelectInput,
  TextInput,
  useGetOne,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { specialistRequests } from "api/specialistApi";
import { HEALTHCARE_COURSES, SpecialistServicePath } from "utils/constants";
import { nameCyrillicRegexValidator, validateDateNotInFuture } from "utils/validations";

// Same rules as the public application form.
const personIdValidator = regex(/^[0-9]{10}$/, "ЕГН/ЛНЧ трябва да съдържа точно 10 цифри");
const contactPhoneValidator = regex(/^(359|0){1}[0-9]{9}$/, "Невалиден телефонен номер");

// Length limits enforced by the backend (HealthcareCampaignApplicationProfileExtensions.Validate) -
// checked here so the admin sees which field is wrong instead of the generic error.
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 30;
const UIN_MAX_LENGTH = 20;
const CLINIC_MAX_LENGTH = 300;
const EMAIL_MAX_LENGTH = 200;

// minLength/maxLength skip empty values, so the optional secondName may still be "".
const nameValidators = [minLength(NAME_MIN_LENGTH), maxLength(NAME_MAX_LENGTH), nameCyrillicRegexValidator];

// The desired date must be in the future and within the campaign's courses year.
const getDesiredDateRange = coursesYear => {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (!coursesYear) return { minDate: tomorrow, maxDate: undefined };

  const yearStart = new Date(coursesYear, 0, 1);
  return {
    minDate: yearStart > tomorrow ? yearStart : tomorrow,
    maxDate: new Date(coursesYear, 11, 31),
  };
};

const CreateForm = ({ campaign }) => {
  const { setValue } = useFormContext();
  const courseId = useWatch({ name: "courseId" });

  // hasDesiredDate is only returned by the course details endpoint.
  const { data: course } = useGetOne(
    SpecialistServicePath + HEALTHCARE_COURSES,
    { id: courseId },
    { enabled: !!courseId },
  );
  const hasDesiredDate = !!course?.hasDesiredDate;
  const courseYear = course?.year ?? campaign?.coursesYear;
  const { minDate, maxDate } = getDesiredDateRange(courseYear);

  // Backend rejects a desired date for fixed-date courses.
  useEffect(() => {
    if (!hasDesiredDate) setValue("desiredDate", null);
  }, [courseId, hasDesiredDate, setValue]);

  const validateDesiredDate = value => {
    if (!value) return undefined;
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    if (date < minDate || (maxDate && date > maxDate)) {
      return `Датата трябва да е в бъдещето и в рамките на ${courseYear} г.`;
    }
    return undefined;
  };

  return (
    <Grid
      container
      spacing={2}
    >
      {/* Own full-width row: the names below must always start a new line, with or without the date. */}
      <Grid
        item
        xs={12}
      >
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <SelectInput
              source="courseId"
              label="Курс здравна грижа"
              choices={campaign?.courses ?? []}
              optionText="nameBg"
              emptyText={false}
              validate={required()}
            />
          </Grid>
          {hasDesiredDate && (
            <Grid
              item
              xs={12}
              md={6}
            >
              <CustomDateInput
                source="desiredDate"
                label="Желана начална дата"
                noTime
                minDate={minDate}
                maxDate={maxDate}
                validate={[required(), validateDesiredDate]}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="firstName"
          label="Име"
          validate={[required(), ...nameValidators]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="secondName"
          label="Презиме"
          validate={nameValidators}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="lastName"
          label="Фамилия"
          validate={[required(), ...nameValidators]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="personId"
          label="ЕГН/ЛНЧ"
          validate={[required(), personIdValidator]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="uin"
          label="УИН"
          validate={[required(), maxLength(UIN_MAX_LENGTH)]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <CustomDateInput
          source="birthDate"
          label="Дата на раждане"
          noTime
          maxDate={new Date()}
          validate={[required(), validateDateNotInFuture]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="speciality"
          reference={specialistRequests.getHealthcareSpecialitySelect()}
        >
          <SelectInput
            optionText="nameBg"
            label="Специалност на кандидата"
            emptyText={false}
            validate={required()}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="clinic"
          label="Клиника"
          validate={[required(), maxLength(CLINIC_MAX_LENGTH)]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="email"
          label="Имейл"
          validate={[required(), email(), maxLength(EMAIL_MAX_LENGTH)]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="contactPhone"
          label="Телефон"
          validate={[required(), contactPhoneValidator]}
        />
      </Grid>
    </Grid>
  );
};

export default CreateForm;
